import { Feature } from "@xapp/components";
import { getProduct, Product } from "./getProducts";

// Dev fallback product ID (used for all tiers in development)
const DEV_PRODUCT_ID = "prod_QgzDK1DTytsTCl";

// 2026 Production Standard tier product IDs
const STARTER_PROD_ID = "prod_UgEc1b0knbSMjR";
const GROWTH_PROD_ID = "prod_UgZaYP5ml5xPtk";
const PRO_PROD_ID = "prod_UgZaeolkHTLite";

// 2026 Production ServiceTitan variant product IDs
const STARTER_ST_PROD_ID = "prod_UgZdnfsMRTrElG";
const GROWTH_ST_PROD_ID = "prod_UgcBcWB8Ri7kZT";
const PRO_ST_PROD_ID = "prod_UgcBYVy9rnObPM";

const isDev = __ENV__ === "development";

// Exported IDs resolve to the correct environment
export const STARTER_ID = isDev ? DEV_PRODUCT_ID : STARTER_PROD_ID;
export const GROWTH_ID = isDev ? DEV_PRODUCT_ID : GROWTH_PROD_ID;
export const PRO_ID = isDev ? DEV_PRODUCT_ID : PRO_PROD_ID;
export const STARTER_ST_ID = isDev ? DEV_PRODUCT_ID : STARTER_ST_PROD_ID;
export const GROWTH_ST_ID = isDev ? DEV_PRODUCT_ID : GROWTH_ST_PROD_ID;
export const PRO_ST_ID = isDev ? DEV_PRODUCT_ID : PRO_ST_PROD_ID;

export type TierName = "starter" | "growth" | "pro";
export type TierVariant = "standard" | "servicetitan";

export interface TierFeatures {
    unlimitedBookingLinks: boolean;
    standardAnalytics: boolean;
    leadAnalysis: boolean;
    aiWebChat: boolean;
    actionBar: boolean;
    advancedAnalytics: boolean;
    customAnalyticsViaAI: boolean;
    investorReporting: boolean;
    promptBasedReporting: boolean;
}

export interface PricingTier2026 {
    tier: TierName;
    variant: TierVariant;
    /** Stripe product ID (env-aware: dev fallback in development) */
    productId: string;
    /** Display name for the tier */
    displayName: string;
    /** Display price string */
    displayPrice: string;
    features: TierFeatures;
}

const STARTER_FEATURES: TierFeatures = {
    unlimitedBookingLinks: true,
    standardAnalytics: true,
    leadAnalysis: true,
    aiWebChat: false,
    actionBar: false,
    advancedAnalytics: false,
    customAnalyticsViaAI: false,
    investorReporting: false,
    promptBasedReporting: false,
};

const GROWTH_FEATURES: TierFeatures = {
    unlimitedBookingLinks: true,
    standardAnalytics: true,
    leadAnalysis: true,
    aiWebChat: true,
    actionBar: true,
    advancedAnalytics: false,
    customAnalyticsViaAI: false,
    investorReporting: false,
    promptBasedReporting: false,
};

const PRO_FEATURES: TierFeatures = {
    unlimitedBookingLinks: true,
    standardAnalytics: true,
    leadAnalysis: true,
    aiWebChat: true,
    actionBar: true,
    advancedAnalytics: true,
    customAnalyticsViaAI: true,
    investorReporting: true,
    promptBasedReporting: true,
};

export const PRICING_TIERS_2026: PricingTier2026[] = [
    { tier: "starter", variant: "standard", productId: STARTER_ID, displayName: "Starter", displayPrice: "$99 / month", features: STARTER_FEATURES },
    { tier: "starter", variant: "servicetitan", productId: STARTER_ST_ID, displayName: "Starter for ServiceTitan", displayPrice: "$199 / month", features: STARTER_FEATURES },
    { tier: "growth", variant: "standard", productId: GROWTH_ID, displayName: "Growth", displayPrice: "$249 / month", features: GROWTH_FEATURES },
    { tier: "growth", variant: "servicetitan", productId: GROWTH_ST_ID, displayName: "Growth for ServiceTitan", displayPrice: "$349 / month", features: GROWTH_FEATURES },
    { tier: "pro", variant: "standard", productId: PRO_ID, displayName: "Pro", displayPrice: "$399 / month", features: PRO_FEATURES },
    { tier: "pro", variant: "servicetitan", productId: PRO_ST_ID, displayName: "Pro for ServiceTitan", displayPrice: "$499 / month", features: PRO_FEATURES },
];

const ALL_2026_IDS = new Set(PRICING_TIERS_2026.map((t) => t.productId));

export function is2026Tier(productId: string): boolean {
    return ALL_2026_IDS.has(productId);
}

export function get2026Tier(productId: string): PricingTier2026 | undefined {
    return PRICING_TIERS_2026.find((t) => t.productId === productId);
}

export function get2026TierByName(tierName: TierName, variant: TierVariant = "standard"): PricingTier2026 | undefined {
    return PRICING_TIERS_2026.find((t) => t.tier === tierName && t.variant === variant);
}

export function get2026StandardTiers(): (PricingTier2026 & { product: Product })[] {
    return PRICING_TIERS_2026.filter((t) => t.variant === "standard").map((t) => ({
        ...t,
        product: getProduct(t.productId),
    }));
}

export function get2026ServiceTitanTiers(): (PricingTier2026 & { product: Product })[] {
    return PRICING_TIERS_2026.filter((t) => t.variant === "servicetitan").map((t) => ({
        ...t,
        product: getProduct(t.productId),
    }));
}

const FEATURE_LABELS: Record<keyof TierFeatures, string> = {
    unlimitedBookingLinks: "Unlimited Booking Links",
    standardAnalytics: "Standard Analytics",
    leadAnalysis: "Lead Analysis",
    aiWebChat: "AI Web Chat (MCP)",
    actionBar: "Action Bar",
    advancedAnalytics: "Advanced Analytics",
    customAnalyticsViaAI: "Custom Analytics via AI",
    investorReporting: "Investor/Board-Ready Reporting",
    promptBasedReporting: "Prompt Based Reporting",
};

export function tierFeaturesToFeatureList(features: TierFeatures, options?: { onlyIncluded?: boolean }): Feature[] {
    const entries = (Object.keys(FEATURE_LABELS) as (keyof TierFeatures)[]).map((key) => ({
        title: FEATURE_LABELS[key],
        available: features[key],
    }));

    if (options?.onlyIncluded) {
        return entries.filter((f) => f.available);
    }

    return entries;
}
